import { useEffect } from 'react';
import { DimensionType, DimensionValueGroupingType } from '@sleed/bizcommerce-api';
import { useForm, Controller } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useDimensionGroupingMutations, useDimensionGroupingTypes } from '~/hooks/use-dimension-groupings';
import { useDimensionsList } from '~/hooks/use-dimensions';
import { dimensionGroupingSchema, EMPTY_DIMENSION_GROUPING, type DimensionGroupingFormValues } from '~/lib/schemas/dimension-grouping';

const NO_DIMENSION = '__none__';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: DimensionGroupingFormValues | null;
}

/** Add / edit a dimension value grouping (Ομαδοποίηση Διαστάσεων). */
export function DimensionGroupingEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useDimensionGroupingMutations();
	const { data: types } = useDimensionGroupingTypes();
	// BW-8412: a grouping without a type may belong to a Custom dimension (dt 0)
	const { data: dimensions } = useDimensionsList();
	const customDimensions = (dimensions ?? []).filter(d => d.dt === DimensionType.Custom);
	const form = useForm<DimensionGroupingFormValues>({
		resolver: zodResolver(dimensionGroupingSchema) as Resolver<DimensionGroupingFormValues>,
		defaultValues: initial ?? EMPTY_DIMENSION_GROUPING,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, control, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_DIMENSION_GROUPING);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('attributes.dimensionGroupings.editTitle') : t('attributes.dimensionGroupings.addTitle')}</DialogTitle>
					<DialogDescription>{t('attributes.dimensionGroupings.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('attributes.dimensionGroupings.columns.name')} error={formState.errors.nm?.message}>
						<Input {...register('nm')} maxLength={100} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
					</FormField>
					<FormField label={t('attributes.dimensionGroupings.columns.code')} error={formState.errors.cd?.message}>
						<Input {...register('cd')} maxLength={100} aria-invalid={Boolean(formState.errors.cd)} />
					</FormField>
					<FormField label={t('attributes.dimensionGroupings.columns.erpCode')} error={formState.errors.erpcd?.message}>
						<Input {...register('erpcd')} maxLength={50} aria-invalid={Boolean(formState.errors.erpcd)} />
					</FormField>
					<FormField label={t('attributes.dimensionGroupings.columns.type')} error={formState.errors.dgt?.message}>
						<Controller
							control={control}
							name="dgt"
							render={({ field }) => (
								<Select value={field.value != null ? String(field.value) : ''} onValueChange={v => field.onChange(Number(v))}>
									<SelectTrigger aria-invalid={Boolean(formState.errors.dgt)}>
										<SelectValue placeholder={t('attributes.dimensionGroupings.selectType')} />
									</SelectTrigger>
									<SelectContent>
										{(types ?? []).map(ty => (
											<SelectItem key={ty.id} value={String(ty.id)}>
												{ty.nm}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							)}
						/>
					</FormField>
					{watch('dgt') === DimensionValueGroupingType.None && (
						<FormField label={t('attributes.dimensionGroupings.columns.dimension')}>
							<Controller
								control={control}
								name="did"
								render={({ field }) => (
									<Select
										value={field.value != null ? String(field.value) : NO_DIMENSION}
										onValueChange={v => field.onChange(v === NO_DIMENSION ? null : Number(v))}>
										<SelectTrigger>
											<SelectValue placeholder={t('attributes.dimensionGroupings.noDimension')} />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value={NO_DIMENSION}>{t('attributes.dimensionGroupings.noDimension')}</SelectItem>
											{customDimensions.map(d => (
												<SelectItem key={d.id} value={String(d.id)}>
													{d.nm}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							/>
						</FormField>
					)}
					<FormField label={t('attributes.dimensionGroupings.columns.color')} error={formState.errors.chex?.message}>
						<div className="flex items-center gap-2">
							<input
								type="color"
								value={watch('chex') || '#000000'}
								onChange={e => setValue('chex', e.target.value, { shouldDirty: true })}
								className="h-9 w-12 cursor-pointer rounded-md border border-border bg-surface p-1"
								aria-label={t('attributes.dimensionGroupings.columns.color')}
							/>
							<Input
								value={watch('chex') ?? ''}
								onChange={e => setValue('chex', e.target.value || null, { shouldDirty: true })}
								maxLength={10}
								placeholder="#RRGGBB"
							/>
						</div>
					</FormField>
					<div className="grid grid-cols-2 gap-3">
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="dg-iv">{t('attributes.dimensionGroupings.columns.visible')}</Label>
							<Switch id="dg-iv" checked={watch('iv')} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
						</div>
						<FormField label={t('attributes.dimensionGroupings.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
